<template>
  <section id="shorts-section" test_id="shorts-section">
    <Card class="tw-space-y-4">
      <div class="tw-flex tw-items-center tw-justify-between">
        <h2 class="tw-heading-2">Shorts</h2>
        <div v-if="carouselRef" class="tw-flex tw-items-center tw-space-x-2">
          <IconButton
            @click="carouselRef.previousSlide"
            :disabled="carouselRef.isBeginning"
          >
            <BaseIcon name="arrow-sm-left" />
          </IconButton>
          <IconButton
            @click="carouselRef.nextSlide"
            :disabled="carouselRef.isEnd"
          >
            <BaseIcon name="arrow-sm-right" />
          </IconButton>
        </div>
      </div>
      <BaseCarousel
        ref="carouselRef"
        :breakpoints="{
          1: { slidesPerView: 1 },
          992: { slidesPerView: 2 },
          1200: { slidesPerView: 3 },
        }"
        :slides-data="mentorShortsWithModalNames"
        :is-navigation-enabled="false"
        class="tw-cursor-grab active:tw-cursor-grabbing"
      >
        <template
          #slide="{ slideData }: { slideData: MentorShortWithModalName }"
        >
          <button
            @click="openModal(slideData.modalName)"
            class="tw-relative tw-block tw-h-full tw-w-full tw-overflow-hidden tw-rounded-xl tw-bg-neutral-50 tw-bg-cover tw-bg-center tw-px-5 tw-pb-5 tw-pt-9 tw-text-left tw-text-white"
          >
            <img
              src="@/assets/images/video-shorts-background.png"
              alt=""
              class="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover"
            />

            <div class="tw-relative tw-flex tw-h-full tw-flex-col">
              <p class="tw-text-xl tw-font-semibold tw-leading-6">
                {{ slideData.title }}
              </p>
              <p v-if="slideData.quote" class="tw-mt-6 tw-text-sm">
                {{ slideData.quote }}
              </p>
              <div
                class="tw-mt-24 tw-flex tw-grow tw-flex-wrap tw-items-end tw-justify-between tw-gap-2"
              >
                <BaseIcon name="btn-play" class="tw-h-6 tw-w-6" />
                <span class="tw-font-semibold">
                  {{ formatDuration(slideData.duration) }}
                </span>
              </div>
            </div>

            <BaseModal
              :name="slideData.modalName"
              width-class="tw-max-w-5xl tw-overflow-hidden tw-bg-transparent"
            >
              <iframe
                class="tw-aspect-video tw-w-full"
                :src="`https://www.youtube.com/embed/${slideData.youtubeId}`"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              />
            </BaseModal>
          </button>
        </template>
      </BaseCarousel>
    </Card>
  </section>
</template>

<script setup lang="ts">
import BaseCarousel from '@/shared/components/BaseCarousel.vue'
import BaseIcon from '@/shared/components/BaseIcon.vue'
import { computed, ref } from 'vue'
import Card from '@/shared/components/Card.vue'
import IconButton from '@/shared/components/IconButton.vue'
import { MentorShort } from '@/modules/growthmentor/mentors/types/mentor-short.ts'
import BaseModal from '@/shared/components/BaseModal.vue'
import { useModals } from '@/shared/composables/useModals.js'
import { formatDuration } from '@/shared/utils/videoDuration.js'

const props = defineProps<{
  mentorShorts: MentorShort[]
}>()

const { openModal } = useModals()

const carouselRef = ref(null)

type MentorShortWithModalName = MentorShort & { modalName: string }
const mentorShortsWithModalNames = computed<MentorShortWithModalName[]>(() =>
  props.mentorShorts.map((mentorShort) => ({
    ...mentorShort,
    modalName: `mentor-short-preview-${mentorShort.id}`,
  })),
)
</script>
